import { useState, useEffect, useCallback } from "react";
import papi, { formatErr } from "@/lib/partnerApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  LayoutDashboard, Library, Boxes, AlertCircle, Cpu, Plus, Search, Upload, Loader2,
  LogOut, Check, Trash2, Pencil, X, FileSpreadsheet, ShieldCheck, Brain, ClipboardList, Lock,
  Ruler, ScrollText, FileText,
} from "lucide-react";

const TEAL = "#0E9AC8";
const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "library", label: "Knowledge Library", icon: Library },
  { key: "mia", label: "MIA — Materials", icon: Boxes, domain: "MIA" },
  { key: "dia", label: "DIA — Design", icon: Ruler, domain: "DIA" },
  { key: "qia", label: "QIA — BOQ", icon: ClipboardList, domain: "QIA" },
  { key: "bylaw", label: "NCR Bylaws", icon: ScrollText, domain: "BYLAW" },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "gaps", label: "Knowledge Gaps", icon: AlertCircle },
  { key: "agents", label: "Agents & Settings", icon: Cpu },
];
const DOMAIN_VIEWS = { mia: "MIA", dia: "DIA", qia: "QIA", bylaw: "BYLAW" };
const VIEW_TITLES = { library: "Knowledge Library", mia: "MIA — Material Intelligence", dia: "DIA — Design Intelligence", qia: "QIA — Quantity Intelligence", bylaw: "NCR Bylaw Intelligence" };
const SOON = ["Cost & Rates", "Sources & Versions", "Analytics"];
const STATUS_STYLES = {
  published: "bg-emerald-50 text-emerald-600", review: "bg-amber-50 text-amber-600",
  draft: "bg-slate-100 text-slate-500", archived: "bg-slate-100 text-slate-400",
};

/* ============================= AUTH GATE ============================= */
function KcLogin({ onDone }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await papi.post("/partner/auth/login", { email, password });
      const role = res.data?.user?.role;
      if (role !== "admin") { toast.error("Knowledge Center is restricted to the Super Admin."); setBusy(false); return; }
      localStorage.setItem("partner_token", res.data.token);
      localStorage.setItem("partner_role", role);
      onDone();
    } catch (e2) { toast.error(formatErr(e2.response?.data?.detail) || "Login failed"); setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-[#0B1B2B] flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-sm" data-testid="kc-login-form">
        <div className="flex items-center gap-2.5 mb-8">
          <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ background: TEAL }}>
            <Brain className="h-6 w-6 text-white" strokeWidth={1.75} />
          </div>
          <div>
            <p className="text-white font-display font-bold text-lg leading-tight">NCIS™ Knowledge Center</p>
            <p className="text-white/50 text-xs">Internal · Super Admin only</p>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-6 space-y-4">
          <div>
            <Label>Email</Label>
            <Input data-testid="kc-login-email" className="rounded-xl mt-1.5" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@nirmantra.ai" />
          </div>
          <div>
            <Label>Password</Label>
            <Input data-testid="kc-login-password" type="password" className="rounded-xl mt-1.5" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          <Button data-testid="kc-login-submit" disabled={busy} className="w-full rounded-xl text-white" style={{ background: TEAL }}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Lock className="h-4 w-4 mr-2" /> Enter Knowledge Center</>}
          </Button>
        </div>
      </form>
    </div>
  );
}

/* ============================= ADD / EDIT MODAL ============================= */
const emptyCard = { domain: "GENERAL", type: "knowledge_card", question: "", answer: "", category: "", subcategory: "", city: "", project_type: "", source: "", confidence: "medium", tags: "", status: "draft", title: "" };
const emptyMat = { domain: "MIA", type: "material", city: "", source: "", status: "draft", answer: "", confidence: "medium", material: "", brand: "", grade: "", unit: "50 kg bag", price: "" };

function KnowledgeModal({ record, onClose, onSaved }) {
  const isEdit = !!record?.id;
  const initialMode = record?.type === "material" ? "material" : "card";
  const [mode, setMode] = useState(initialMode);
  const [f, setF] = useState(() => {
    if (record?.id) {
      if (record.type === "material") {
        const d = record.data || {};
        return { ...emptyMat, ...record, ...d, tags: (record.tags || []).join(", ") };
      }
      return { ...emptyCard, ...record, tags: (record.tags || []).join(", ") };
    }
    return record?.domain ? { ...emptyCard, domain: record.domain } : { ...emptyCard };
  });
  const [busy, setBusy] = useState(false);
  const [docFile, setDocFile] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const switchMode = (m) => {
    setMode(m);
    if (m === "material") setF({ ...emptyMat });
    else if (m === "document") setF({ ...emptyCard, type: "document", status: "draft" });
    else setF({ ...emptyCard });
  };

  const save = async () => {
    setBusy(true);
    if (mode === "document") {
      if (!docFile) { toast.error("Choose a file to upload"); setBusy(false); return; }
      const fd = new FormData();
      fd.append("file", docFile); fd.append("domain", f.domain || "GENERAL");
      fd.append("title", f.title || docFile.name); fd.append("source", f.source || "");
      fd.append("status", f.status || "draft");
      try {
        const res = await papi.post("/kc/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`Document added (${res.data.extracted_chars} chars extracted)${res.data.status === "published" ? " · published — NCIS can now read it" : " · draft"}`);
        onSaved();
      } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
      finally { setBusy(false); }
      return;
    }
    let payload;
    if (mode === "material") {
      if (!f.material?.trim()) { toast.error("Material is required"); setBusy(false); return; }
      payload = {
        domain: "MIA", type: "material", title: `${f.material} ${f.brand || ""} ${f.grade || ""}`.trim(),
        city: f.city, source: f.source, status: f.status, answer: f.answer, confidence: f.confidence,
        category: f.category || "", tags: [f.material.toLowerCase()], effective_date: new Date().toISOString().slice(0, 10),
        data: { material: f.material, brand: f.brand, grade: f.grade, unit: f.unit, price: f.price ? parseFloat(f.price) : null, supplier: f.supplier || "" },
      };
    } else {
      if (!f.answer?.trim()) { toast.error("Answer is required"); setBusy(false); return; }
      payload = {
        domain: f.domain, type: "knowledge_card", title: f.title || (f.question || "").slice(0, 80),
        question: f.question, answer: f.answer, category: f.category, subcategory: f.subcategory,
        city: f.city, project_type: f.project_type, source: f.source, confidence: f.confidence, status: f.status,
        tags: (f.tags || "").split(",").map((t) => t.trim()).filter(Boolean), data: {},
      };
    }
    try {
      if (isEdit) await papi.put(`/kc/knowledge/${record.id}`, payload);
      else await papi.post("/kc/knowledge", payload);
      toast.success(isEdit ? "Knowledge updated" : "Knowledge added");
      onSaved();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-xl sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="kc-knowledge-modal">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-slate-900">{isEdit ? "Edit knowledge" : "Add knowledge"}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>
        {!isEdit && (
          <div className="px-6 pt-4 flex gap-2">
            {[["card", "Knowledge Card"], ["material", "Material Data"], ["document", "Document"]].map(([m, l]) => (
              <button key={m} data-testid={`kc-mode-${m}`} onClick={() => switchMode(m)}
                className={`px-4 py-2 rounded-xl text-sm font-medium border ${mode === m ? "text-white border-transparent" : "text-slate-600 border-slate-200"}`}
                style={mode === m ? { background: TEAL } : {}}>{l}</button>
            ))}
          </div>
        )}
        <div className="p-6 space-y-4">
          {mode === "document" ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Assign to domain / agent">
                  <Select value={f.domain} onValueChange={(v) => set("domain", v)}>
                    <SelectTrigger className="rounded-xl mt-1.5" data-testid="kc-doc-domain"><SelectValue /></SelectTrigger>
                    <SelectContent>{["GENERAL", "MIA", "DIA", "QIA", "BYLAW", "VDA", "COST"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Source"><Input className="rounded-xl mt-1.5" value={f.source} onChange={(e) => set("source", e.target.value)} placeholder="Authority / circular" /></Field>
              </div>
              <Field label="Title (optional)"><Input className="rounded-xl mt-1.5" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. GNIDA Building Regulations 2024" /></Field>
              <label className="block border-2 border-dashed border-slate-200 rounded-2xl p-7 text-center cursor-pointer hover:border-[#0E9AC8]">
                <Upload className="h-7 w-7 mx-auto text-slate-400" />
                <p className="mt-2 text-sm font-medium text-slate-700">{docFile ? docFile.name : "Choose PDF, DOCX, XLSX, CSV or TXT"}</p>
                <p className="text-xs text-slate-400 mt-0.5">NCIS reads the extracted text once the document is Published.</p>
                <input type="file" accept=".pdf,.docx,.xlsx,.xls,.csv,.txt" className="hidden" data-testid="kc-doc-file" onChange={(e) => setDocFile(e.target.files[0])} />
              </label>
              <Field label="Status">
                <Select value={f.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger data-testid="kc-doc-status" className="rounded-xl mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{["draft", "review", "published"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </>
          ) : mode === "material" ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Material *"><Input data-testid="kc-f-material" className="rounded-xl mt-1.5" value={f.material} onChange={(e) => set("material", e.target.value)} placeholder="Cement" /></Field>
                <Field label="Brand"><Input className="rounded-xl mt-1.5" value={f.brand} onChange={(e) => set("brand", e.target.value)} placeholder="UltraTech" /></Field>
                <Field label="Grade / Spec"><Input className="rounded-xl mt-1.5" value={f.grade} onChange={(e) => set("grade", e.target.value)} placeholder="OPC 53" /></Field>
                <Field label="Unit"><Input className="rounded-xl mt-1.5" value={f.unit} onChange={(e) => set("unit", e.target.value)} placeholder="50 kg bag" /></Field>
                <Field label="Price (₹)"><Input data-testid="kc-f-price" type="number" className="rounded-xl mt-1.5" value={f.price} onChange={(e) => set("price", e.target.value)} placeholder="425" /></Field>
                <Field label="City *"><Input data-testid="kc-f-city" className="rounded-xl mt-1.5" value={f.city} onChange={(e) => set("city", e.target.value)} placeholder="Noida" /></Field>
              </div>
              <Field label="Notes / context"><Textarea className="rounded-xl mt-1.5" value={f.answer} onChange={(e) => set("answer", e.target.value)} placeholder="Prevailing retail range, applicability…" /></Field>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Domain">
                  <Select value={f.domain} onValueChange={(v) => set("domain", v)}>
                    <SelectTrigger className="rounded-xl mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>{["GENERAL", "MIA", "DIA", "QIA", "VDA", "BYLAW", "COST"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="City / Location"><Input className="rounded-xl mt-1.5" value={f.city} onChange={(e) => set("city", e.target.value)} placeholder="India / NCR / Noida" /></Field>
              </div>
              <Field label="Question"><Input data-testid="kc-f-question" className="rounded-xl mt-1.5" value={f.question} onChange={(e) => set("question", e.target.value)} placeholder="What is the recommended curing period for RCC?" /></Field>
              <Field label="Answer *"><Textarea data-testid="kc-f-answer" className="rounded-xl mt-1.5 min-h-[110px]" value={f.answer} onChange={(e) => set("answer", e.target.value)} placeholder="Knowledge content…" /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Category"><Input className="rounded-xl mt-1.5" value={f.category} onChange={(e) => set("category", e.target.value)} placeholder="Construction" /></Field>
                <Field label="Subcategory"><Input className="rounded-xl mt-1.5" value={f.subcategory} onChange={(e) => set("subcategory", e.target.value)} placeholder="RCC / Concrete" /></Field>
                <Field label="Project type"><Input className="rounded-xl mt-1.5" value={f.project_type} onChange={(e) => set("project_type", e.target.value)} placeholder="Residential" /></Field>
                <Field label="Tags (comma sep)"><Input className="rounded-xl mt-1.5" value={f.tags} onChange={(e) => set("tags", e.target.value)} placeholder="rcc, curing, slab" /></Field>
              </div>
            </>
          )}
          {mode !== "document" && (
          <div className="grid grid-cols-3 gap-4">
            <Field label="Source"><Input className="rounded-xl mt-1.5" value={f.source} onChange={(e) => set("source", e.target.value)} placeholder="Survey / IS code…" /></Field>
            <Field label="Confidence">
              <Select value={f.confidence} onValueChange={(v) => set("confidence", v)}>
                <SelectTrigger className="rounded-xl mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{["high", "medium", "low"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select value={f.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="kc-f-status" className="rounded-xl mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{["draft", "review", "published"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          )}
        </div>
        <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} className="rounded-xl">Cancel</Button>
          <Button data-testid="kc-save-knowledge" onClick={save} disabled={busy} className="rounded-xl text-white" style={{ background: TEAL }}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}
const Field = ({ label, children }) => (<div><Label className="text-xs text-slate-500">{label}</Label>{children}</div>);

/* ============================= EXCEL IMPORT ============================= */
const TARGETS = [["material", "Material *"], ["brand", "Brand"], ["grade", "Grade"], ["unit", "Unit"], ["price", "Price"], ["city", "City"], ["category", "Category"]];
function ImportModal({ onClose, onDone }) {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [parsed, setParsed] = useState(null); // {columns, rows, total}
  const [map, setMap] = useState({});
  const [status, setStatus] = useState("draft");
  const [defaultCity, setDefaultCity] = useState("");

  const onFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await papi.post("/kc/import/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setParsed(res.data);
      const auto = {};
      TARGETS.forEach(([t]) => { const hit = res.data.columns.find((c) => c.toLowerCase() === t || c.toLowerCase().includes(t)); if (hit) auto[t] = hit; });
      setMap(auto);
      setStep(2);
    } catch (e2) { toast.error(formatErr(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doImport = async () => {
    if (!map.material) { toast.error("Map the Material column"); return; }
    setBusy(true);
    const rows = parsed.rows.map((r) => {
      const o = {}; TARGETS.forEach(([t]) => { if (map[t]) o[t] = r[map[t]]; }); return o;
    });
    try {
      const res = await papi.post("/kc/import/materials", { rows, status, default_city: defaultCity, source: "Excel/CSV import" });
      toast.success(`Imported ${res.data.imported} material records`);
      onDone();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl rounded-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="kc-import-modal">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><FileSpreadsheet className="h-5 w-5" style={{ color: TEAL }} /> Import material prices</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6">
          {step === 1 && (
            <label className="block border-2 border-dashed border-slate-200 rounded-2xl p-10 text-center cursor-pointer hover:border-[#0E9AC8]" data-testid="kc-import-dropzone">
              <Upload className="h-8 w-8 mx-auto text-slate-400" />
              <p className="mt-3 font-medium text-slate-700">Upload Excel or CSV</p>
              <p className="text-xs text-slate-400 mt-1">Columns like material, brand, grade, unit, price, city</p>
              <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onFile} data-testid="kc-import-file" />
              {busy && <Loader2 className="h-5 w-5 animate-spin mx-auto mt-3" style={{ color: TEAL }} />}
            </label>
          )}
          {step === 2 && parsed && (
            <div className="space-y-5">
              <p className="text-sm text-slate-500">{parsed.total} rows detected. Map your columns:</p>
              <div className="grid grid-cols-2 gap-3">
                {TARGETS.map(([t, l]) => (
                  <div key={t}>
                    <Label className="text-xs text-slate-500">{l}</Label>
                    <Select value={map[t] || "none"} onValueChange={(v) => setMap((m) => ({ ...m, [t]: v === "none" ? "" : v }))}>
                      <SelectTrigger className="rounded-xl mt-1.5" data-testid={`kc-map-${t}`}><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— none —</SelectItem>
                        {parsed.columns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Default city (if blank)</Label>
                  <Input className="rounded-xl mt-1.5" value={defaultCity} onChange={(e) => setDefaultCity(e.target.value)} placeholder="Noida" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Import as</Label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="rounded-xl mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>{["draft", "review", "published"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="rounded-xl border overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500"><tr>{parsed.columns.slice(0, 6).map((c) => <th key={c} className="px-3 py-2 text-left font-medium">{c}</th>)}</tr></thead>
                  <tbody>{parsed.rows.slice(0, 4).map((r, i) => <tr key={i} className="border-t">{parsed.columns.slice(0, 6).map((c) => <td key={c} className="px-3 py-2 text-slate-600">{String(r[c]).slice(0, 24)}</td>)}</tr>)}</tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} className="rounded-xl">Cancel</Button>
          {step === 2 && <Button data-testid="kc-import-confirm" onClick={doImport} disabled={busy} className="rounded-xl text-white" style={{ background: TEAL }}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Import"}</Button>}
        </div>
      </div>
    </div>
  );
}

/* ============================= RECORD LIST ============================= */
function RecordList({ rows, onEdit, onStatus, onDelete }) {
  if (!rows.length) return <p className="text-sm text-slate-400 py-12 text-center">No knowledge records here yet.</p>;
  return (
    <div className="space-y-2.5">
      {rows.map((r) => (
        <div key={r.id} data-testid={`kc-record-${r.id}`} className="bg-white rounded-xl border p-4 flex items-start gap-3">
          <div className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0 text-white text-[11px] font-bold" style={{ background: r.domain === "MIA" ? TEAL : "#0B1B2B" }}>{r.domain}</div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-slate-900 text-sm">{r.title || r.question || (r.data?.material) || "Untitled"}</span>
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLES[r.status] || "bg-slate-100"}`}>{r.status}</span>
              {r.type === "vendor_price" && <span className="rounded-full bg-indigo-50 text-indigo-600 px-2 py-0.5 text-[11px] font-semibold">vendor</span>}
              <span className="text-[11px] text-slate-400">v{r.version} · {r.confidence}</span>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              {r.type === "material" ? `${r.data?.material || ""} ${r.data?.brand || ""} ${r.data?.grade || ""} · ₹${r.data?.price ?? "—"}/${r.data?.unit || "unit"} · ${r.city || "—"}` : `${r.category || r.domain}${r.city ? " · " + r.city : ""}`}
              {r.source ? ` · src: ${r.source}` : ""}
            </p>
            {r.answer && <p className="text-sm text-slate-600 mt-1.5 line-clamp-2">{r.answer}</p>}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {r.status !== "published" && <button data-testid={`kc-publish-${r.id}`} onClick={() => onStatus(r.id, "published")} title="Publish" className="text-emerald-600 hover:bg-emerald-50 rounded-lg p-1.5"><Check className="h-4 w-4" /></button>}
            {r.status === "published" && <button data-testid={`kc-archive-${r.id}`} onClick={() => onStatus(r.id, "archived")} title="Archive" className="text-slate-400 hover:bg-slate-100 rounded-lg p-1.5"><ClipboardList className="h-4 w-4" /></button>}
            <button onClick={() => onEdit(r)} className="text-slate-400 hover:text-[#0E9AC8] rounded-lg p-1.5"><Pencil className="h-4 w-4" /></button>
            <button onClick={() => onDelete(r.id)} className="text-slate-400 hover:text-red-600 rounded-lg p-1.5"><Trash2 className="h-4 w-4" /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ============================= DOCUMENT UPLOAD ============================= */
function DocUploadModal({ onClose, onDone }) {
  const [domain, setDomain] = useState("GENERAL");
  const [title, setTitle] = useState("");
  const [source, setSource] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const onFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("domain", domain);
      fd.append("title", title || file.name); fd.append("source", source);
      const res = await papi.post("/kc/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(res.data);
      toast.success(`Extracted ${res.data.extracted_chars} chars — saved as draft`);
      onDone();
    } catch (e2) { toast.error(formatErr(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-lg rounded-2xl" onClick={(e) => e.stopPropagation()} data-testid="kc-doc-modal">
        <div className="border-b px-6 py-4 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><FileText className="h-5 w-5" style={{ color: TEAL }} /> Upload document</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Assign to domain / agent">
              <Select value={domain} onValueChange={setDomain}>
                <SelectTrigger className="rounded-xl mt-1.5" data-testid="kc-doc-domain"><SelectValue /></SelectTrigger>
                <SelectContent>{["GENERAL", "MIA", "DIA", "QIA", "BYLAW", "VDA", "COST"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Source"><Input className="rounded-xl mt-1.5" value={source} onChange={(e) => setSource(e.target.value)} placeholder="Authority / circular" /></Field>
          </div>
          <Field label="Title (optional)"><Input className="rounded-xl mt-1.5" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. GNIDA Building Regulations 2024" /></Field>
          <label className="block border-2 border-dashed border-slate-200 rounded-2xl p-8 text-center cursor-pointer hover:border-[#0E9AC8]">
            <Upload className="h-7 w-7 mx-auto text-slate-400" />
            <p className="mt-2 text-sm font-medium text-slate-700">PDF, DOCX, XLSX, CSV or TXT</p>
            <p className="text-xs text-slate-400 mt-0.5">Uploads as Draft — review &amp; publish afterwards.</p>
            <input type="file" accept=".pdf,.docx,.xlsx,.xls,.csv,.txt" className="hidden" onChange={onFile} data-testid="kc-doc-file" />
            {busy && <Loader2 className="h-5 w-5 animate-spin mx-auto mt-2" style={{ color: TEAL }} />}
          </label>
          {preview && <div className="rounded-xl bg-slate-50 border p-3 text-xs text-slate-600 max-h-32 overflow-y-auto"><b>Extracted preview:</b> {preview.preview || "(no text extracted)"}</div>}
        </div>
        <div className="border-t px-6 py-4 flex justify-end"><Button onClick={onClose} className="rounded-xl text-white" style={{ background: TEAL }}>Done</Button></div>
      </div>
    </div>
  );
}

/* ============================= MAIN ============================= */
export default function KnowledgeCenter() {
  const [authed, setAuthed] = useState(localStorage.getItem("partner_token") && localStorage.getItem("partner_role") === "admin");
  const [view, setView] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [rows, setRows] = useState([]);
  const [gaps, setGaps] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null); // record or {}
  const [importOpen, setImportOpen] = useState(false);
  const [docOpen, setDocOpen] = useState(false);
  const [filters, setFilters] = useState({ domain: "", status: "", q: "" });

  const loadOverview = useCallback(async () => { try { const r = await papi.get("/kc/overview"); setOverview(r.data); } catch (e) { if (e.response?.status === 403 || e.response?.status === 401) setAuthed(false); } }, []);
  const loadLibrary = useCallback(async (extra = {}) => {
    setLoading(true);
    try {
      const params = { ...filters, ...extra };
      Object.keys(params).forEach((k) => !params[k] && delete params[k]);
      const r = await papi.get("/kc/knowledge", { params });
      setRows(r.data);
    } catch (e) { if (e.response?.status === 403) setAuthed(false); } finally { setLoading(false); }
  }, [filters]);
  const loadGaps = useCallback(async () => { const r = await papi.get("/kc/gaps"); setGaps(r.data); }, []);
  const loadAgents = useCallback(async () => { const r = await papi.get("/kc/agents"); setAgents(r.data); }, []);

  useEffect(() => {
    if (!authed) return;
    if (view === "overview") loadOverview();
    else if (view === "documents") loadLibrary({ type: "document" });
    else if (DOMAIN_VIEWS[view]) loadLibrary({ domain: DOMAIN_VIEWS[view] });
    else if (view === "library") loadLibrary();
    else if (view === "gaps") loadGaps();
    else if (view === "agents") loadAgents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, view]);

  const refresh = () => {
    setModal(null); setImportOpen(false); setDocOpen(false);
    if (view === "documents") loadLibrary({ type: "document" });
    else if (DOMAIN_VIEWS[view]) loadLibrary({ domain: DOMAIN_VIEWS[view] });
    else if (view === "library") loadLibrary();
    else if (view === "overview") loadOverview();
  };
  const setStatus = async (id, status) => { await papi.post(`/kc/knowledge/${id}/status`, { status }); toast.success(`Marked ${status}`); refresh(); };
  const del = async (id) => { if (!window.confirm("Delete this record?")) return; await papi.delete(`/kc/knowledge/${id}`); toast.success("Deleted"); refresh(); };
  const resolveGap = async (id) => { await papi.post(`/kc/gaps/${id}/status`, { status: "resolved" }); loadGaps(); };

  if (!authed) return <KcLogin onDone={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-[#F6F7F9] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[#0B1B2B] text-white flex-col hidden md:flex fixed h-screen">
        <div className="px-5 py-5 flex items-center gap-2.5 border-b border-white/10">
          <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: TEAL }}><Brain className="h-5 w-5 text-white" strokeWidth={1.75} /></div>
          <div><p className="font-display font-bold text-sm leading-tight">NCIS™</p><p className="text-white/50 text-[11px]">Knowledge Center</p></div>
        </div>
        <div className="p-4">
          <Button data-testid="kc-add-knowledge" onClick={() => setModal({})} className="w-full rounded-xl text-white font-semibold" style={{ background: TEAL }}><Plus className="h-4 w-4 mr-1.5" /> Add Knowledge</Button>
        </div>
        <nav className="px-3 space-y-1 flex-1 overflow-y-auto">
          {NAV.map((n) => (
            <button key={n.key} data-testid={`kc-nav-${n.key}`} onClick={() => setView(n.key)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${view === n.key ? "bg-white/10 text-white font-medium" : "text-white/60 hover:text-white hover:bg-white/5"}`}>
              <n.icon className="h-4 w-4" strokeWidth={1.75} /> {n.label}
            </button>
          ))}
          <p className="px-3 pt-4 pb-1 text-[10px] uppercase tracking-wider text-white/30">Phase 2</p>
          {SOON.map((s) => (<div key={s} className="flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-white/25 cursor-not-allowed">{s}</div>))}
        </nav>
        <button onClick={() => { localStorage.removeItem("partner_token"); localStorage.removeItem("partner_role"); setAuthed(false); }} className="m-4 flex items-center gap-2 text-white/50 hover:text-white text-sm"><LogOut className="h-4 w-4" /> Sign out</button>
      </aside>

      {/* Main */}
      <main className="flex-1 md:ml-64 p-6 lg:p-8">
        {/* mobile nav */}
        <div className="md:hidden mb-4 flex gap-2 overflow-x-auto">
          {NAV.map((n) => <button key={n.key} onClick={() => setView(n.key)} className={`px-3 py-2 rounded-lg text-xs whitespace-nowrap ${view === n.key ? "text-white" : "bg-white text-slate-600"}`} style={view === n.key ? { background: TEAL } : {}}>{n.label}</button>)}
        </div>

        {view === "overview" && <Overview o={overview} />}

        {(view === "library" || DOMAIN_VIEWS[view]) && (
          <div>
            <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
              <h1 className="font-display text-2xl font-bold text-slate-900">{VIEW_TITLES[view] || "Knowledge Library"}</h1>
              <div className="flex gap-2">
                {view === "mia" && <Button data-testid="kc-import-open" onClick={() => setImportOpen(true)} variant="outline" className="rounded-xl"><FileSpreadsheet className="h-4 w-4 mr-1.5" /> Import Excel/CSV</Button>}
                <Button data-testid="kc-add-knowledge-2" onClick={() => setModal(view === "mia" ? { type: "material" } : DOMAIN_VIEWS[view] ? { domain: DOMAIN_VIEWS[view] } : {})} className="rounded-xl text-white" style={{ background: TEAL }}><Plus className="h-4 w-4 mr-1.5" /> Add</Button>
              </div>
            </div>
            <div className="flex gap-2 mb-4 flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input data-testid="kc-search" className="rounded-xl pl-9" placeholder="Search knowledge…" value={filters.q}
                  onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                  onKeyDown={(e) => e.key === "Enter" && loadLibrary(DOMAIN_VIEWS[view] ? { domain: DOMAIN_VIEWS[view] } : {})} />
              </div>
              <Select value={filters.status || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === "all" ? "" : v }))}>
                <SelectTrigger className="rounded-xl w-36" data-testid="kc-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>{["all", "draft", "review", "published", "archived"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              <Button variant="outline" className="rounded-xl" onClick={() => loadLibrary(DOMAIN_VIEWS[view] ? { domain: DOMAIN_VIEWS[view] } : {})}>Apply</Button>
            </div>
            {loading ? <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" style={{ color: TEAL }} /></div>
              : <RecordList rows={rows} onEdit={setModal} onStatus={setStatus} onDelete={del} />}
          </div>
        )}

        {view === "documents" && (
          <div>
            <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
              <div>
                <h1 className="font-display text-2xl font-bold text-slate-900">Documents</h1>
                <p className="text-sm text-slate-500 mt-1">Upload PDFs/DOCX/XLSX/CSV/TXT, assign a domain, then review &amp; publish. Only published documents are used by NCIS™.</p>
              </div>
              <Button data-testid="kc-doc-upload-open" onClick={() => setDocOpen(true)} className="rounded-xl text-white shrink-0" style={{ background: TEAL }}><Upload className="h-4 w-4 mr-1.5" /> Upload document</Button>
            </div>
            {loading ? <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" style={{ color: TEAL }} /></div>
              : <RecordList rows={rows} onEdit={setModal} onStatus={setStatus} onDelete={del} />}
          </div>
        )}

        {view === "gaps" && (
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900 mb-2">Knowledge Gaps</h1>
            <p className="text-sm text-slate-500 mb-5">Questions NCIS™ could not confidently answer from verified knowledge — a queue to improve NCIS.</p>
            {gaps.length === 0 ? <p className="text-sm text-slate-400 py-12 text-center">No open knowledge gaps. 🎉</p> : (
              <div className="space-y-2.5">
                {gaps.map((g) => (
                  <div key={g.id} data-testid={`kc-gap-${g.id}`} className="bg-white rounded-xl border p-4 flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2"><span className="rounded-full bg-amber-50 text-amber-600 px-2 py-0.5 text-[11px] font-semibold">{g.detected_domain}</span><span className={`rounded-full px-2 py-0.5 text-[11px] ${g.status === "open" ? "bg-red-50 text-red-500" : "bg-emerald-50 text-emerald-600"}`}>{g.status}</span></div>
                      <p className="font-medium text-slate-900 text-sm mt-1.5">{g.question}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{g.missing}</p>
                    </div>
                    {g.status === "open" && <Button size="sm" variant="outline" className="rounded-lg shrink-0" onClick={() => resolveGap(g.id)}>Resolve</Button>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {view === "agents" && (
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900 mb-2">Agent Registry</h1>
            <p className="text-sm text-slate-500 mb-5">Specialist intelligence agents. NCIS™ routes questions to the relevant agent's verified knowledge first.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {agents.map((a) => (
                <div key={a.code} data-testid={`kc-agent-${a.code}`} className="bg-white rounded-xl border p-4 flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg flex items-center justify-center text-white text-xs font-bold" style={{ background: a.active ? TEAL : "#94a3b8" }}>{a.code}</div>
                  <div className="flex-1"><p className="font-semibold text-slate-900 text-sm">{a.name}</p><p className="text-xs text-slate-500">{a.description}</p></div>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${a.active ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>{a.active ? "Active" : "Phase 2"}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 rounded-xl border bg-white p-4 flex items-center gap-3 text-sm text-slate-500">
              <ShieldCheck className="h-5 w-5" style={{ color: TEAL }} /> Only <b className="text-slate-700 mx-1">Published</b> knowledge is used in production NCIS™ answers. This center is Super-Admin only.
            </div>
          </div>
        )}
      </main>

      {modal && <KnowledgeModal record={modal.id || modal.type || modal.domain ? modal : {}} onClose={() => setModal(null)} onSaved={refresh} />}
      {importOpen && <ImportModal onClose={() => setImportOpen(false)} onDone={refresh} />}
      {docOpen && <DocUploadModal onClose={() => setDocOpen(false)} onDone={refresh} />}
    </div>
  );
}

function Overview({ o }) {
  if (!o) return <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" style={{ color: TEAL }} /></div>;
  const cards = [
    ["Total records", o.total], ["Published", o.by_status.published], ["Draft", o.by_status.draft],
    ["Material records", o.materials], ["Vendor pending", o.vendor_pending], ["Open gaps", o.gaps],
  ];
  return (
    <div data-testid="kc-overview">
      <h1 className="font-display text-2xl font-bold text-slate-900">NCIS™ Knowledge Health</h1>
      <p className="text-sm text-slate-500 mt-1">How much verified intelligence Nirmantra has taught NCIS™.</p>
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map(([l, v]) => (
          <div key={l} className="bg-white rounded-2xl border p-5" data-testid={`kc-stat-${l.replace(/\s/g, "-").toLowerCase()}`}>
            <p className="text-xs text-slate-500">{l}</p>
            <p className="mt-1 font-display text-3xl font-extrabold text-slate-900">{v ?? 0}</p>
          </div>
        ))}
      </div>
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-2xl border p-5">
          <p className="font-semibold text-slate-900 text-sm mb-3">Knowledge by domain</p>
          <div className="space-y-2">
            {Object.entries(o.by_domain).map(([d, c]) => (
              <div key={d} className="flex items-center gap-3">
                <span className="text-xs text-slate-500 w-16">{d}</span>
                <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ background: TEAL, width: `${Math.min(100, (c / Math.max(1, o.total)) * 100)}%` }} /></div>
                <span className="text-xs text-slate-600 w-8 text-right">{c}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-2xl border p-5">
          <p className="font-semibold text-slate-900 text-sm mb-3">Recent updates</p>
          <div className="space-y-2.5">
            {(o.recent_updates || []).map((r) => (
              <div key={r.id} className="flex items-center gap-2 text-sm">
                <span className="rounded px-1.5 py-0.5 text-[10px] font-bold text-white" style={{ background: r.domain === "MIA" ? TEAL : "#0B1B2B" }}>{r.domain}</span>
                <span className="text-slate-700 truncate flex-1">{r.title || r.question || r.data?.material || "Untitled"}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_STYLES[r.status] || ""}`}>{r.status}</span>
              </div>
            ))}
            {(!o.recent_updates || o.recent_updates.length === 0) && <p className="text-sm text-slate-400">No records yet — add your first knowledge card.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
